import apiClient from './apiClient';
import { auth } from '@/config/firebase';
import { useAppStateStore } from '@/store/appStateStore';

/**
 * Auth-related backend API calls.
 * Mirrors Flutter WebHomeRepository / IAuthRepository backend calls
 * that happen during/after login.
 */

export interface CandidateDetails {
  primaryEmailId?: string;
  primaryContactNumber?: string;
  primaryContactNumberCountryCode?: string;
  workspaceId?: string;
  isMfaEnabled?: boolean;
  [key: string]: unknown;
}

/**
 * GET /v1/candidate
 * Mirrors Flutter IWebHomeRepository.getUserDetails(), which reloads the Firebase user
 * first so emailVerified is current.
 */
export async function getUserDetails(): Promise<CandidateDetails> {
  await auth.currentUser?.reload();
  const res = await apiClient.get<CandidateDetails>('v1/candidate');
  // Header name — Flutter AppStateNotifier.setCandidateDetails
  useAppStateStore.getState().setCandidateDetails(res.data);
  return res.data;
}

/**
 * PUT /v1/candidate
 * Mirrors Flutter IWebHomeRepository.updateUserDetails().
 * Flutter sends the payload as an array: dio.put(url, data: [request]). The request is
 * CandidateDetails.toJson() (see profileApi candidateDetailsToJson), or toApiRequest()
 * when Flutter passes apiRequest: true (see toCandidateApiRequest).
 */
export async function updateUserDetails(
  candidateDetails: Record<string, unknown>,
): Promise<void> {
  await apiClient.put('v1/candidate', [candidateDetails]);
}

/** The CandidateDetails fields Flutter's toJson() / toApiRequest() send (candidate_details.dart). */
const CANDIDATE_REQUEST_KEYS = [
  'id', 'currentRole', 'currentLocation', 'primaryEmailId', 'secondaryEmailId',
  'primaryContactNumber', 'secondaryContactNumber', 'displayId', 'firstName', 'lastName',
  'workspaceId', 'linkedInUrl', 'candidateUrl', 'experienceInMonths',
  'secondaryContactNumberCountryCode', 'primaryContactNumberCountryCode', 'resumeUpdateOn',
] as const;

/**
 * Mirrors Flutter CandidateDetails.fromJson(fields).toApiRequest() — the payload of
 * updateUserDetails(apiRequest: true): only the fields that are set (not null, '' or 0),
 * plus isMfaEnabled when it is set.
 */
export function toCandidateApiRequest(fields: Record<string, unknown>): Record<string, unknown> {
  const request: Record<string, unknown> = {};
  for (const key of CANDIDATE_REQUEST_KEYS) {
    const value = fields[key];
    if (value != null && value !== '' && value !== 0) request[key] = value;
  }
  if (fields.isMfaEnabled != null) request.isMfaEnabled = fields.isMfaEnabled;
  return request;
}

/**
 * GET /v1/p/forms/theme/applyApplication
 * Mirrors Flutter IWebHomeRepository.getApplyApplicationFormContent().
 * Endpoint: {noAuth}/forms/theme/applyApplication — Flutter calls it through the
 * interceptor, so the token is sent when a user is signed in.
 * Pre-fetched after login to cache form schema.
 */
export async function getApplyApplicationFormContent(): Promise<unknown> {
  const res = await apiClient.get('v1/p/forms/theme/applyApplication');
  localStorage.setItem('applyFormContent', JSON.stringify(res.data));
  return res.data;
}

/**
 * GET /v1/p/language/constants (language header = 'easyApplyApplicationForm')
 * Mirrors Flutter IWebHomeRepository.getEasyApplyFormContent() —
 * getHeaders(languageValue: 'easyApplyApplicationForm') with Authorization removed,
 * sent with plain http (no HttpInterCeptor).
 */
export async function getEasyApplyFormContent(): Promise<unknown> {
  const res = await apiClient.get('v1/p/language/constants', {
    noAuth: true,
    skipErrorInterceptor: true,
    headers: { language: 'easyApplyApplicationForm' },
  });
  localStorage.setItem('easyApplyFormContent', JSON.stringify(res.data));
  return res.data;
}

/**
 * GET /v1/p/forms/theme/editProfile
 * Mirrors Flutter IWebHomeRepository.getEditProfileFormContent().
 * Endpoint: {noAuth}/forms/theme/editProfile — token sent when signed in (interceptor).
 */
export async function getEditProfileFormContent(): Promise<unknown> {
  const res = await apiClient.get('v1/p/forms/theme/editProfile');
  localStorage.setItem('editProfileFormContent', JSON.stringify(res.data));
  return res.data;
}

/**
 * Pre-fetch dynamic form content after login.
 * Mirrors Flutter WebLoginBloc._OnGetDynamicFormsContent handler.
 */
export async function prefetchDynamicForms(): Promise<void> {
  await Promise.allSettled([
    getApplyApplicationFormContent(),
    getEasyApplyFormContent(),
    getEditProfileFormContent(),
  ]);
}
